/**
 * A country's flag, as a rectangle.
 *
 * WHY NOT THE EMOJI. A flag emoji is a CHARACTER, so its shape belongs to the
 * system font: Apple draws them waving, Windows draws nothing at all and falls
 * back to the two letters. Neither is ours to change. These are 3x2 SVGs, so
 * they look the same on every machine and they are rectangles.
 *
 * WHY VENDORED, NOT A DEPENDENCY. They come from `country-flag-icons`, which
 * unpacks to 5.8 MB for every country on earth in several formats. Seventeen
 * files at 68 KB total sit in ./flags instead — no install, no supply chain,
 * and no version to track for artwork that does not change.
 *
 * Globbed rather than imported one by one, so adding a country to DIAL_CODES
 * and dropping its SVG in the folder is the whole job; there is no import list
 * to forget to update.
 */
const FLAG_URLS = import.meta.glob<string>('./flags/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
});

function urlFor(iso: string): string | undefined {
  // `iso` is required, and one caller resolving it is why. This guarded an
  // absent code for a while: the wizard's country field is newer than the
  // drafts it restores, so every session in flight arrived with none and the
  // page crashed here. But `PhoneField` now resolves the country ONCE before
  // any of the four lookups run, so nothing can reach this undefined — and a
  // second fallback that no test can fail is the same "four answers to one
  // question" this file's caller was fixed to stop having.
  return FLAG_URLS[`./flags/${iso.toUpperCase()}.svg`];
}

export interface FlagIconProps {
  /** ISO 3166-1 alpha-2. */
  iso: string;
  /** Country name, for the alt text. */
  title: string;
}

export function FlagIcon({ iso, title }: FlagIconProps) {
  const src = urlFor(iso);
  // A missing file leaves the row alone rather than rendering a broken image:
  // the country name is right beside it and carries the meaning by itself.
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      // Decorative. The country is spelled out next to it, so announcing the
      // flag as well would say the same thing twice.
      aria-hidden
      title={title}
      width={20}
      height={14}
      className="shrink-0 rounded-xs ring-1 ring-border"
    />
  );
}
